import { Globe } from "lucide-react";
import { useRouterState } from "@tanstack/react-router";
import {
  LOCALES,
  LOCALE_FLAG,
  LOCALE_LABEL,
  localizePath,
  type Locale,
} from "@/i18n/config";
import { useLocale } from "@/i18n/useLocale";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { useT } from "@/i18n/useLocale";

const SWITCH_LABEL: Record<Locale, string> = {
  en: "Loading…",
  no: "Laster…",
  "zh-Hant": "載入中…",
};

/**
 * Paints a plain, inline-styled overlay that survives the moment between the
 * click and the new document, so the reader never sees a half-styled page.
 */
function showSwitchOverlay(target: Locale) {
  if (typeof document === "undefined") return;
  const root = document.createElement("div");
  root.setAttribute(
    "style",
    "position:fixed;inset:0;z-index:2147483647;display:flex;align-items:center;justify-content:center;" +
      "flex-direction:column;gap:14px;background:#fff;color:#111;font-size:14px;letter-spacing:0.02em;",
  );
  const spinner = document.createElement("div");
  spinner.setAttribute(
    "style",
    "width:26px;height:26px;border-radius:9999px;border:2px solid rgba(0,0,0,0.15);" +
      "border-top-color:#111;animation:byteline-locale-spin 0.7s linear infinite;",
  );
  const style = document.createElement("style");
  style.textContent = "@keyframes byteline-locale-spin{to{transform:rotate(360deg)}}";
  const label = document.createElement("span");
  label.textContent = SWITCH_LABEL[target] ?? SWITCH_LABEL.en;
  root.append(style, spinner, label);
  document.body.append(root);
}

/**
 * Switching language is a full document load so the server renders the page
 * in the new language (and search engines see a real, separate URL).
 */
const LocaleSwitcher = ({ className, compact = false }: { className?: string; compact?: boolean }) => {
  const t = useT();
  const locale = useLocale();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  const hrefFor = (target: Locale) => {
    // location.pathname is already language-free (the router strips the prefix).
    const clean = pathname.length > 1 ? pathname.replace(/\/$/, "") : "/";
    return localizePath(clean, target);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className={cn(
          "flex items-center gap-2 rounded-lg px-2 py-1.5 text-[13px] text-muted-foreground",
          "hover:bg-accent/50 hover:text-foreground transition-colors",
          className,
        )}
        aria-label={t("auto.change-language-91d6")}
      >
        <Globe className="h-4 w-4 shrink-0" />
        {!compact && <span>{LOCALE_LABEL[locale]}</span>}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        {LOCALES.map((item) => (
          <DropdownMenuItem key={item} asChild>
            <a
              href={hrefFor(item)}
              hrefLang={item}
              className="flex items-center gap-2"
              onClick={(event) => {
                if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
                event.preventDefault();
                showSwitchOverlay(item);
                window.location.assign(hrefFor(item));
              }}
            >
              <span aria-hidden>{LOCALE_FLAG[item]}</span>
              <span className={cn(item === locale && "font-semibold")}>{LOCALE_LABEL[item]}</span>
            </a>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default LocaleSwitcher;
